// --- React
import React, { useState } from 'react';
// --- UI Components
import Input from '@dtci/spellbook/Input';
import { Col, Row } from 'antd';
import Typography from '@dtci/spellbook/Typography';
import Select from '@dtci/spellbook/Select';
import Button from '@dtci/spellbook/Button';
import { InputLabel } from '../../../InputLabel/InputLabel';
import { EditorComponent } from './editor';
// --- Styles
import './styles.scss';
// --- Utilities
import moment from 'moment';
import { commentTypes, getCommentTypeName } from '../../../../utils/commentType';
// --- Redux
import { useSelector } from 'react-redux';
import { useCommentsActions } from '../../../../store/comments/useComments';
import {datastageApi} from "../../../../api";
import {useLoading} from "../../../../utils/hooks/useLoading";

/**
 * CreateCommentForm Component
 *
 * This component is responsible for rendering the form used to create or edit comments.
 * It includes fields for the comment title, type, and body.
 * It also allows users to toggle between public and private comments.
 *
 * @component
 * @returns {JSX.Element} The rendered CreateCommentForm component.
 */
export function CreateCommentForm() {
    const [iconType, setIconType] = useState('fa-eye');
    const [isPublic, setIsPublic] = useState(true);
    const { comment } = useSelector((s) => s.comments);
    const { updateCommentsFilterForm } = useCommentsActions();
    const { setLoadingTrue, setLoadingFalse } = useLoading();

    // Function to handle the toggle between public and private comments
    const togglePublicPrivate = () => {
        const nextIsPublic = !isPublic;
        setIsPublic(nextIsPublic);
        setIconType(nextIsPublic ? 'fa-eye' : 'fa-lock');
        updateCommentsFilterForm({
            key: 'comment',
            data: { public: true },
        });
    };

    const downloadAttachment = async (rightsLineId, fileName) => {
        setLoadingTrue();
        const res = await datastageApi.downloadAttachment(rightsLineId, fileName);
        saveAs(res.data, fileName);
        setLoadingFalse();
    };

    return (
        <>
            <div className="comment-header-row">
                <Button
                    iconType={`fa-solid ${iconType}`}
                    labelText="Export"
                    styleType="link"
                    onClick={togglePublicPrivate}
                >
                    {isPublic ? 'Public' : 'Private'}
                </Button>
            </div>

            <div className="comment-info-row">
                <div className="comment-title-col">
                    <InputLabel colProps={{ span: 22 }} label="Comment Title">
                        <Input
                            value={comment.title}
                            onChange={(e) =>
                                updateCommentsFilterForm({
                                    key: 'comment',
                                    data: { title: e.target.value },
                                })
                            }
                            width="auto"
                            id="title"
                            name="title"
                            placeholder="Enter"
                        />
                    </InputLabel>
                </div>
                <div className="comment-modifiedby-col">
                    <Typography variant="T30">
                        {comment.modifiedBy && comment.modifiedBy.fullName}
                    </Typography>
                </div>
                <div className="comment-modifiedat-col">
                    <Typography variant="T30">
                        {comment.modifiedAt &&
                            moment(comment.modifiedAt.toString().substring(0, 10)).format(
                                'YYYY-MM-DD',
                            )}
                    </Typography>
                </div>
            </div>

            <div className="comment-select-row">
                <Select
                    colProps={{ span: 22 }}
                    onChange={(value) =>
                        updateCommentsFilterForm({
                            key: 'comment',
                            data: { commentType: value?.value },
                        })
                    }
                    name="commentType"
                    options={commentTypes}
                    isClearable
                    isSearchable
                    customClear={false}
                    value={{
                        label: getCommentTypeName(comment.commentType),
                        value: comment.commentType,
                    }}
                />
            </div>

            <div className="comment-editor-row">
                <EditorComponent />
            </div>

            {comment?.attachments && (
                <div className="comment-attachments-row">
                    {comment.attachments.map((doc) => (
                        <>
                            <a
                                key={window.crypto.randomUUID()}
                                className="comment-attachment-link"
                                onClick={(e) => {
                                    e.preventDefault();
                                    downloadAttachment(doc?.rightslineId, doc?.attachmentName)
                                }}
                            >
                                {doc.attachmentName}
                            </a>
                            <br />
                            <br />
                        </>
                    ))}
                </div>
            )}
        </>
    );
}
